interface OfflineAssessmentData {
  assessmentData: any;
  responses: any[];
  duration: number;
  savedAt: string;
  type: string;
}

const OFFLINE_ASSESSMENTS_KEY = 'offlineAssessments';

/**
 * Get all offline assessments from localStorage
 */
const getAllOfflineAssessments = (): Record<string, OfflineAssessmentData> => {
  try {
    const data = localStorage.getItem(OFFLINE_ASSESSMENTS_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {

    return {};
  }
};

/**
 * Save all offline assessments to localStorage
 */
const saveAllOfflineAssessments = (assessments: Record<string, OfflineAssessmentData>): void => {
  try {
    localStorage.setItem(OFFLINE_ASSESSMENTS_KEY, JSON.stringify(assessments));
  } catch (error) {

  }
};

/**
 * Save assessment data to localStorage for offline users
 * @param data The assessment data to save
 * @param type The type of assessment (e.g., 'dass-star', 'dass1')
 */
export const saveOfflineAssessment = (data: OfflineAssessmentData, type?: string): void => {
  try {
    const assessments = getAllOfflineAssessments();
    const assessmentType = type || data.type || 'dass-star'; // Default to 'dass-star' for backward compatibility

    assessments[assessmentType] = { ...data, type: assessmentType };
    saveAllOfflineAssessments(assessments);
  } catch (error) {

  }
};

/**
 * Get saved offline assessment data from localStorage by type
 * @param type The type of assessment to retrieve (e.g., 'dass-star', 'dass1')
 * @returns The assessment data or null if not found
 */
export const getOfflineAssessment = (type: string = 'dass-star'): OfflineAssessmentData | null => {
  try {
    const assessments = getAllOfflineAssessments();
    const data = assessments[type];

    if (data) {
      return data;
    }
    return null;
  } catch (error) {

    return null;
  }
};

/**
 * Clear offline assessment data from localStorage by type
 * @param type The type of assessment to clear (e.g., 'dass-star', 'dass1')
 */
export const clearOfflineAssessment = (type: string = 'dass-star'): void => {
  try {
    const assessments = getAllOfflineAssessments();
    delete assessments[type];
    saveAllOfflineAssessments(assessments);

  } catch (error) {

  }
};

/**
 * Get all offline assessments that need to be synced
 * @returns Record of assessment type to data
 */
export const getAllQueuedAssessments = (): Record<string, OfflineAssessmentData> => {
  return getAllOfflineAssessments();
};

/**
 * Clear all offline assessments from localStorage
 */
export const clearAllOfflineAssessments = (): void => {
  try {
    localStorage.removeItem(OFFLINE_ASSESSMENTS_KEY);

  } catch (error) {

  }
};

/**
 * Check if there's offline assessment data waiting to be synced for a specific type
 * @param type The type of assessment to check (e.g., 'dass-star', 'dass1')
 */
export const hasOfflineAssessment = (type: string = 'dass-star'): boolean => {
  const assessments = getAllOfflineAssessments();
  return !!assessments[type];
};

/**
 * Check if there are any offline assessments waiting to be synced
 */
export const hasAnyOfflineAssessments = (): boolean => {
  const assessments = getAllOfflineAssessments();
  return Object.keys(assessments).length > 0;
};


